import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { rideAPI } from '../../lib/api';
import { formatCurrency, getStatusColor, getStatusText, formatDateTime } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import LiveMap from '../../components/LiveMap';
import PaymentFlow from '../../components/PaymentFlow';
import RateRide from '../../components/RateRide';
import PromoCodeInput from '../../components/PromoCodeInput';
import RideChat from '../../components/RideChat';
import TripReceipt from '../../components/TripReceipt';
import { MapPin, Clock, User, Car, DollarSign, X, Loader2, Gift, Star, Receipt } from 'lucide-react';
import { toast } from 'sonner';

const RideDetails = () => {
  const { rideId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ride, setRide] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [tipping, setTipping] = useState(false);
  const [selectedTip, setSelectedTip] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);

  const tipOptions = [
    { percent: 0.15, label: '15%' },
    { percent: 0.20, label: '20%' },
    { percent: 0.25, label: '25%' },
    { percent: 0.50, label: '50%' },
    { percent: 1.0, label: '100%' },
    { percent: 2.0, label: '200%' },
  ];

  useEffect(() => {
    loadRide();
    const interval = setInterval(loadRide, 5000);
    return () => clearInterval(interval);
  }, [rideId]);

  const loadRide = async () => {
    try {
      const response = await rideAPI.getRide(rideId);
      setRide(response.data);
    } catch (error) {
      console.error('Error loading ride:', error);
      toast.error('Ride not found');
      navigate('/rider/history');
    } finally {
      setLoading(false);
    }
  };

  const cancelRide = async () => {
    setCancelling(true);
    try {
      await rideAPI.cancelRide(rideId);
      toast.success('Ride cancelled');
      loadRide();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel ride');
    } finally {
      setCancelling(false);
    }
  };

  const addTip = async (tipPercent) => {
    setTipping(true);
    setSelectedTip(tipPercent);
    try {
      await rideAPI.addTip(rideId, tipPercent);
      toast.success('Tip added! Your driver will appreciate it.');
      loadRide();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add tip');
    } finally {
      setTipping(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  if (!ride) return null;

  const canCancel = ['requested', 'scheduled'].includes(ride.status);
  const canTip = ride.status === 'completed' && !ride.tip_amount;

  return (
    <div className="p-6" data-testid="ride-details-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Badge className={`${getStatusColor(ride.status)} font-semibold uppercase text-sm mb-2`}>
              {getStatusText(ride.status)}
            </Badge>
            <h1 className="text-2xl font-display font-bold text-slate-900 dark:text-white uppercase tracking-tight">
              Ride Details
            </h1>
          </div>
          <div className="text-right">
            <p className="text-3xl font-display font-bold text-slate-900 dark:text-white">
              {formatCurrency(ride.actual_fare || ride.estimated_fare)}
            </p>
            {ride.tip_amount > 0 && (
              <p className="text-sm text-green-600">+{formatCurrency(ride.tip_amount)} tip</p>
            )}
          </div>
        </div>

        {/* Live Map */}
        <Card className="mb-6 bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 overflow-hidden">
          <LiveMap
            pickup={ride.pickup_location}
            dropoff={ride.dropoff_location}
            driverLocation={ride.driver_location}
            showRoute={true}
            height="250px"
          />
        </Card>

        {/* Locations */}
        <Card className="mb-6 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <MapPin className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Pickup</p>
                  <p className="font-medium text-slate-900 dark:text-white">
                    {ride.pickup_location?.address || `${ride.pickup_location?.lat?.toFixed(4)}, ${ride.pickup_location?.lng?.toFixed(4)}`}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <MapPin className="w-4 h-4 text-red-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Dropoff</p>
                  <p className="font-medium text-slate-900 dark:text-white">
                    {ride.dropoff_location?.address || `${ride.dropoff_location?.lat?.toFixed(4)}, ${ride.dropoff_location?.lng?.toFixed(4)}`}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Trip Details */}
        <Card className="mb-6 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="font-display uppercase tracking-tight">Trip Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl text-center">
                <p className="text-xs text-slate-500 dark:text-slate-400 uppercase">Distance</p>
                <p className="text-xl font-display font-bold text-slate-900 dark:text-white">
                  {ride.distance_miles?.toFixed(1)} mi
                </p>
              </div>
              <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl text-center">
                <p className="text-xs text-slate-500 dark:text-slate-400 uppercase">Duration</p>
                <p className="text-xl font-display font-bold text-slate-900 dark:text-white">
                  {ride.duration_minutes} min
                </p>
              </div>
            </div>

            {ride.loyalty_discount > 0 && (
              <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-center gap-2">
                <Gift className="w-5 h-5 text-green-600" />
                <span className="text-sm text-green-700 dark:text-green-400">
                  Loyalty discount applied: -{formatCurrency(ride.loyalty_discount)}
                </span>
              </div>
            )}

            {ride.scheduled_time && (
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-600" />
                <span className="text-sm text-blue-700 dark:text-blue-400">
                  Scheduled for {formatDateTime(ride.scheduled_time)}
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Driver Info */}
        {ride.driver && (
          <Card className="mb-6 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="font-display uppercase tracking-tight">Your Driver</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center">
                  <User className="w-7 h-7 text-slate-400" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900 dark:text-white">{ride.driver.full_name}</p>
                  <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400">
                    <span className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-amber-500" />
                      {ride.driver.rating?.toFixed(1)}
                    </span>
                    <span>
                      {ride.driver.vehicle_color} {ride.driver.vehicle_make} {ride.driver.vehicle_model}
                    </span>
                  </div>
                  <p className="text-sm font-mono text-slate-500 dark:text-slate-400">
                    {ride.driver.license_plate}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tip Section */}
        {canTip && (
          <Card className="mb-6 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="font-display uppercase tracking-tight">Add a Tip</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                100% of tips go directly to your driver
              </p>
              <div className="grid grid-cols-3 gap-3">
                {tipOptions.map((option) => (
                  <Button
                    key={option.percent}
                    variant="outline"
                    onClick={() => addTip(option.percent)}
                    disabled={tipping}
                    className={`
                      tip-btn h-16 flex flex-col
                      ${selectedTip === option.percent ? 'selected' : ''}
                    `}
                    data-testid={`tip-btn-${option.label}`}
                  >
                    {tipping && selectedTip === option.percent ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <span className="text-lg font-bold">{option.label}</span>
                        <span className="text-xs text-slate-500">
                          {formatCurrency((ride.actual_fare || ride.estimated_fare) * option.percent)}
                        </span>
                      </>
                    )}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment Section - Show for completed rides */}
        {ride.status === 'completed' && (
          <div className="mb-6">
            <PaymentFlow ride={ride} onPaymentComplete={loadRide} />
          </div>
        )}

        {/* Rating Section - Show for completed rides */}
        {ride.status === 'completed' && (
          <div className="mb-6">
            <RateRide ride={ride} onRatingSubmitted={loadRide} />
          </div>
        )}

        {/* Receipt Button - Show for completed rides */}
        {ride.status === 'completed' && (
          <Button
            onClick={() => setShowReceipt(true)}
            variant="outline"
            className="w-full h-12 rounded-full border-slate-300 text-slate-700 hover:bg-slate-50 font-semibold mb-4"
            data-testid="view-receipt-btn"
          >
            <Receipt className="w-4 h-4 mr-2" />
            View Receipt
          </Button>
        )}

        {/* Promo Code Section - Show for pending rides that haven't started */}
        {['requested', 'scheduled', 'accepted'].includes(ride.status) && !ride.promo_applied && (
          <div className="mb-6">
            <PromoCodeInput
              rideAmount={ride.estimated_fare}
              rideId={ride.id}
              onPromoApplied={loadRide}
            />
          </div>
        )}

        {/* Cancel Button */}
        {canCancel && (
          <Button
            onClick={cancelRide}
            disabled={cancelling}
            variant="outline"
            className="w-full h-12 rounded-full border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20 font-semibold"
            data-testid="cancel-ride-btn"
          >
            {cancelling ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <X className="w-4 h-4 mr-2" />
                Cancel Ride
              </>
            )}
          </Button>
        )}
      </div>

      {/* Chat Component - Show for active rides with a driver */}
      {ride.driver && ['accepted', 'in_progress'].includes(ride.status) && (
        <RideChat rideId={ride.id} otherPartyName={ride.driver.full_name} />
      )}

      {/* Receipt Modal */}
      {showReceipt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <TripReceipt rideId={ride.id} onClose={() => setShowReceipt(false)} />
        </div>
      )}
    </div>
  );
};

export default RideDetails;
